import { apiFetch } from "./api";
import { compressImage } from "./upload";
import type {
  IssueAttachmentUploadUrlRequest,
  IssueAttachmentUploadUrlResponse,
} from "@family/shared";

const ALLOWED_MIMES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
]);
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Upload one screenshot for an issue, mirroring the avatar flow:
 *   1. Compress client-side (mobile screenshots are big PNGs)
 *   2. POST /families/:slug/issues/:issueId/attachments/upload-url
 *   3. PUT bytes directly to GCS
 *   4. POST .../attachments/finalize
 */
export async function uploadIssueAttachment(
  familySlug: string,
  issueId: string,
  file: File,
): Promise<void> {
  const compressed = await compressImage(file);

  if (!ALLOWED_MIMES.has(compressed.type)) {
    throw new Error("Screenshots must be PNG, JPEG, WebP, or GIF.");
  }
  if (compressed.size > MAX_BYTES) {
    throw new Error("Each screenshot must be 5 MB or smaller.");
  }

  const reqBody: IssueAttachmentUploadUrlRequest = {
    mimeType: compressed.type as IssueAttachmentUploadUrlRequest["mimeType"],
    fileSize: compressed.size,
  };

  const signed = await apiFetch<IssueAttachmentUploadUrlResponse>(
    `/families/${familySlug}/issues/${issueId}/attachments/upload-url`,
    { method: "POST", body: JSON.stringify(reqBody) },
  );

  const putRes = await fetch(signed.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": compressed.type },
    body: compressed,
  });
  if (!putRes.ok) {
    const text = await putRes.text().catch(() => "");
    throw new Error(`Upload failed: ${putRes.status} ${text}`);
  }

  await apiFetch(
    `/families/${familySlug}/issues/${issueId}/attachments/finalize`,
    { method: "POST", body: JSON.stringify({ storagePath: signed.storagePath }) },
  );
}
